import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';
import founderPhoto from '@/assets/jamie.avif';

export function FounderSection() {
  return (
    <section className="py-16 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 network-grid opacity-20" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Built in North Cornelly. <span className="text-gradient-cyan">Engineered for Business.</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            The person behind the promise
          </p>
        </motion.div>

        {/* Glassmorphism Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-8 lg:p-10 overflow-hidden">
            {/* Subtle glow */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-cyan-500/10 blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl" />
            
            <div className="relative flex flex-col lg:flex-row items-center lg:items-start gap-6 md:gap-8 lg:gap-12">
              {/* Circular Avatar */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {/* Glow ring */}
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-500/30 to-orange-500/30 blur-xl scale-110" />
                  <div className="relative w-40 h-40 lg:w-48 lg:h-48 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                    <img 
                      src={founderPhoto} 
                      alt="James Stanton - Founder of SmartSite Solutions" 
                      loading="lazy"
                      decoding="async"
                      width={192}
                      height={192}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                {/* Name & Title below avatar */}
                <div className="text-center mt-6">
                  <h3 className="font-display text-2xl font-bold text-foreground">James Stanton</h3>
                  <p className="text-base text-cyan-400 font-medium">Founder</p>
                </div>
              </div>
              
              {/* Mission Text */}
              <div className="flex-1 space-y-5">
                {/* Quote icon */}
                <Quote className="w-10 h-10 text-cyan-400/50" />
                
                <h3 className="font-display text-2xl lg:text-3xl font-bold text-foreground leading-tight">
                  Fast Websites. Fair Prices. No Nonsense.
                </h3>
                
                <div className="space-y-3 md:space-y-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                  <p>
                    I didn't spend the last 20 years hiding behind a screen. I spent them working face-to-face with customers in retail and insurance. I know that business owners don't care about code—they care about communication and cash flow.
                  </p>
                  <p>
                    I founded SmartSite Solutions right here in North Cornelly to bridge the gap. I use the latest AI technology to build high-performance sites, but I run the business with old-school Welsh reliability. No jargon. No ghosting. Just results.
                  </p>
                </div>
                
                {/* Key stats */}
                <div className="flex flex-wrap gap-6 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-2xl font-bold text-cyan-400">🤝</p>
                    <p className="text-xs text-muted-foreground">Service Expert</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400">90+</p>
                    <p className="text-xs text-muted-foreground">PageSpeed Score</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-400">5★</p>
                    <p className="text-xs text-muted-foreground">Google Reviews</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
